/* Instructor class console — a faithful port of the prototype's class dashboard.
 *
 * Owner-only view of one class: KPI tiles + five tabs — Leaderboard (cumulative
 * balanced-scorecard ranking), Students (roster with progress/quiz/journals),
 * Teams (membership + company), Grading (weighted component grade per student
 * with an override), and Settings (editable name / length / grade weights /
 * brief). Reads the /classes/:id/overview endpoint; grade and settings edits go
 * to the API. CSV export is built client-side from the overview. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useClassOverview, useSetGrade, useUpdateClass, useResetStudentPassword } from '../hooks/queries';
import { Spinner, EmptyNote } from '../components/ui';
import { money } from '../lib/format';
import type { ClassOverview } from '../lib/types';
import { StudentReview } from './StudentReview';
import { InstructorTutorial } from '../tutorial/InstructorTutorial';
import { useTutorial } from '../tutorial/useTutorial';
import { instructorSeries } from '../tutorial/steps.instructor';
import { shapeOf } from '../tutorial/types';
import '../styles/instructor.css';

type Tab = 'leaderboard' | 'students' | 'teams' | 'grading' | 'settings';
const TABS: [Tab, string][] = [
  ['leaderboard', '🏆 Leaderboard'], ['students', '👥 Students'], ['teams', '🧑‍🤝‍🧑 Teams'],
  ['grading', '🎯 Grading'], ['settings', '⚙️ Settings'],
];
const medal = (i: number) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : String(i + 1));
const score = (v: number | null) => (v == null ? '—' : String(Math.round(v)));

function exportCsv(o: ClassOverview) {
  const head = ['Student', 'Email', 'Company', 'Strategy', 'Quarter', 'Revenue', 'CumProfit', 'Share', 'Brand', 'Composite', 'Quiz', 'Journals', 'Grade'];
  const rows = o.students.map((s) => [
    s.name, s.email, s.company ?? '', s.strategy ?? '', `${s.round}/${s.maxRounds}`,
    s.revenue ?? '', s.cumProfit ?? '', s.share != null ? (s.share * 100).toFixed(1) + '%' : '',
    s.brand != null ? Math.round(s.brand) : '', s.composite != null ? Math.round(s.composite) : '',
    `${s.quizDone}/${s.quizTotal}`, s.journalCount, s.grade.total != null ? s.grade.total.toFixed(1) : '',
  ]);
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${o.class.name.replace(/[^\w-]+/g, '_')}-grades.csv`; a.click();
  URL.revokeObjectURL(url);
}

export function InstructorClass() {
  const { id } = useParams<{ id: string }>();
  const q = useClassOverview(id);
  const setGrade = useSetGrade(id!);
  const updateClass = useUpdateClass(id!);
  const [tab, setTab] = useState<Tab>('leaderboard');
  const [reviewUserId, setReviewUserId] = useState<string | null>(null);
  const [ov, setOv] = useState<Record<string, string>>({});
  const resetPw = useResetStudentPassword(id);
  const [resetFor, setResetFor] = useState<{ userId: string; tempPassword: string } | null>(null);
  const [form, setForm] = useState<{ name: string; simLength: number; brief: string; w: Record<string, number> } | null>(null);

  // Error first: a failed load also has no data, so the spinner check would
  // otherwise swallow it and spin forever.
  if (q.isError) return <EmptyNote>Couldn’t load this class — it may not exist or you may not own it.</EmptyNote>;
  if (q.isLoading || !q.data) return <Spinner label="Loading class…" />;
  const o = q.data;

  const playing = o.students.filter((s) => s.started);
  const leaderboard = [...playing].sort((a, b) => (b.composite ?? 0) - (a.composite ?? 0));
  const weights = o.class.gradeWeights;

  // ── Leaderboard ────────────────────────────────────────────────────────────
  const leaderboardTab = () => (
    <div className="card">
      <h3>Class ranking — cumulative balanced scorecard</h3>
      {leaderboard.length === 0 ? (
        <div className="empty-state"><div className="ic">⏳</div>No results yet — students appear here after their first quarter.</div>
      ) : (
        <table className="tbl">
          <thead><tr><th>#</th><th>Student / Company</th><th className="num">Qtr</th><th className="num">Revenue</th><th className="num">Cum. profit</th><th className="num">Share</th><th className="num">Brand</th><th className="num">Score</th><th></th></tr></thead>
          <tbody>
            {leaderboard.map((s, i) => (
              <tr key={s.userId} className={i === 0 ? 'rank-1' : ''}>
                <td>{medal(i)}</td>
                <td><b>{s.name}</b><div className="sub">{s.company ?? '—'}{s.strategy ? ' · ' + s.strategy : ''}</div></td>
                <td className="num">{s.round}/{s.maxRounds}</td>
                <td className="num">{money(s.revenue)}</td>
                <td className={'num ' + ((s.cumProfit ?? 0) >= 0 ? 'pos' : 'neg')}>{money(s.cumProfit)}</td>
                <td className="num">{s.share != null ? (s.share * 100).toFixed(1) + '%' : '—'}</td>
                <td className="num">{s.brand != null ? Math.round(s.brand) : '—'}</td>
                <td className="num"><b style={{ fontSize: 15 }}>{score(s.composite)}</b></td>
                {/* Tour anchor on the top row only: the spotlight takes the last
                    match in DOM order, which put it on the bottom of the table. */}
                <td className="num"><button className="btn btn-ghost btn-sm" data-tut={i === 0 ? 'ic-review' : undefined} onClick={() => setReviewUserId(s.userId)}>Review →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ── Students ────────────────────────────────────────────────────────────────
  const studentsTab = () => (
    <div className="card">
      {o.students.length === 0 ? (
        <div className="empty-state"><div className="ic">👥</div>No students yet. Share the join code <b>{o.class.joinCode}</b>.</div>
      ) : (
        <table className="tbl">
          <thead><tr><th>Student</th><th>Team</th><th className="num">Progress</th><th className="num">Quiz</th><th className="num">Journals</th><th className="num">Score</th><th></th></tr></thead>
          <tbody>
            {o.students.map((s) => (
              <tr key={s.userId}>
                <td><b>{s.name}</b>{s.company ? <div className="sub">{s.company}</div> : null}</td>
                <td className="sub">{s.teamName ?? <span style={{ color: 'var(--warn)' }}>unassigned</span>}</td>
                <td className="num">{s.finished ? <span className="chip good">finished</span> : s.started ? `${s.round}/${s.maxRounds} qtrs` : <span className="chip warn">not started</span>}</td>
                <td className="num">{s.quizTotal ? `${s.quizDone}/${s.quizTotal}` : '—'}</td>
                <td className="num">{s.journalCount}</td>
                <td className="num">{score(s.composite)}</td>
                <td className="num">
                  <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setReviewUserId(s.userId)}>Review →</button>
                    <button className="btn btn-ghost btn-sm" disabled={resetPw.isPending}
                      title="Reset this student's password to a temporary one (logs them out everywhere)"
                      onClick={() => {
                        if (!window.confirm(`Reset ${s.name}'s password? Their current password and sessions stop working immediately.`)) return;
                        resetPw.mutate(s.userId, { onSuccess: (r) => setResetFor({ userId: s.userId, tempPassword: r.tempPassword }) });
                      }}>🔑 Reset</button>
                  </div>
                  {resetFor?.userId === s.userId && (
                    <div className="sub" style={{ marginTop: 4, textAlign: 'right' }}>
                      Temp password: <b style={{ color: 'var(--acc)', userSelect: 'all' }}>{resetFor.tempPassword}</b>
                      <div style={{ color: 'var(--warn)' }}>Shown once — pass it to the student now.</div>
                    </div>
                  )}
                  {resetPw.isError && resetPw.variables === s.userId && (
                    <div className="sub" style={{ marginTop: 4, textAlign: 'right', color: 'var(--bad)' }}>{(resetPw.error as Error).message}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ── Teams ─────────────────────────────────────────────────────────────────
  const teamsTab = () => (
    <div className="card">
      <h3>Teams</h3>
      {o.teams.length === 0 ? (
        <EmptyNote>No teams yet — students form or join teams from their dashboard.</EmptyNote>
      ) : (
        <table className="tbl">
          <thead><tr><th>Team</th><th>Members</th><th>Company</th><th className="num">Qtr</th><th className="num">Score</th></tr></thead>
          <tbody>
            {o.teams.map((t) => (
              <tr key={t.id}>
                <td><b>{t.name}</b></td>
                <td className="sub">{t.members.map((m) => m.name + (m.role ? ` (${m.role})` : '')).join(', ') || '—'}</td>
                <td>{t.company ?? '—'}{t.insolvent ? <span className="chip bad" style={{ marginLeft: 6 }} title="Cumulative losses have exhausted this company's book equity">insolvency watch{t.insolventSince ? ` · Q${t.insolventSince}` : ''}</span> : null}</td>
                <td className="num">{t.round}/{t.maxRounds}</td>
                <td className="num">{score(t.composite)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ── Grading ──────────────────────────────────────────────────────────────────
  const gradingTab = () => (
    <div className="card">
      <h3>Grading</h3>
      <div className="sub" style={{ marginBottom: 10 }}>
        Weighted grade per student — scorecard {weights.scorecard ?? 0}% · quiz {weights.quiz ?? 0}% · journal {weights.journal ?? 0}% (edit weights in Settings). Enter a number to override a computed grade; clear it to revert.
      </div>
      {setGrade.error ? <div className="sub" style={{ color: 'var(--bad)', marginBottom: 10 }}>{(setGrade.error as Error).message}</div> : null}
      <table className="tbl">
        <thead><tr><th>Student</th><th className="num">Scorecard</th><th className="num">Quiz</th><th className="num">Journal</th><th className="num">Total</th><th>Override</th></tr></thead>
        <tbody>
          {o.students.map((s) => {
            const gc = s.grade.components;
            return (
              <tr key={s.userId}>
                <td><b>{s.name}</b></td>
                <td className="num">{Math.round(gc.scorecard ?? 0)}</td>
                <td className="num">{Math.round(gc.quiz ?? 0)}</td>
                <td className="num">{Math.round(gc.journal ?? 0)}</td>
                <td className="num"><b>{s.grade.total != null ? s.grade.total.toFixed(1) : '—'}</b>{s.grade.override ? <span className="chip warn" style={{ marginLeft: 6 }}>manual</span> : null}</td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <input className="fin-input" type="number" min={0} max={100} style={{ width: 80 }} placeholder="—"
                      value={ov[s.userId] ?? ''} onChange={(e) => setOv((p) => ({ ...p, [s.userId]: e.target.value }))} />
                    <button className="btn btn-ghost btn-sm" disabled={setGrade.isPending || ov[s.userId] == null || ov[s.userId] === ''}
                      onClick={() => {
                        const v = parseFloat(ov[s.userId] ?? '');
                        // NaN serialises to null, which the API reads as "clear the override".
                        if (!Number.isFinite(v)) return;
                        setGrade.mutate({ userId: s.userId, body: { total: Math.max(0, Math.min(100, v)) } });
                        setOv((p) => ({ ...p, [s.userId]: '' }));
                      }}>Set</button>
                    {s.grade.override ? <button className="btn btn-ghost btn-sm" disabled={setGrade.isPending} onClick={() => setGrade.mutate({ userId: s.userId, body: { total: null } })}>Clear</button> : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // ── Settings ───────────────────────────────────────────────────────────────
  const f = form ?? { name: o.class.name, simLength: o.class.simLength, brief: o.class.brief ?? '', w: { scorecard: weights.scorecard ?? 70, quiz: weights.quiz ?? 15, journal: weights.journal ?? 15 } };
  const setF = (patch: Partial<typeof f>) => setForm({ ...f, ...patch });
  const settingsTab = () => (
    <div className="card" style={{ maxWidth: 560 }}>
      <h3>Class settings</h3>
      <div className="field"><label>Class name</label><input className="txt" style={{ width: '100%' }} value={f.name} onChange={(e) => setF({ name: e.target.value })} /></div>
      <div className="field"><label>Game length (quarters)</label><input className="txt" type="number" min={1} max={40} style={{ width: 120 }} value={f.simLength} onChange={(e) => setF({ simLength: Math.max(1, Math.min(40, +e.target.value || 1)) })} /></div>
      <div className="field">
        <label>Grade weights (%)</label>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          {(['scorecard', 'quiz', 'journal'] as const).map((k) => (
            <label key={k} className="sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{k}
              <input className="fin-input" type="number" min={0} max={100} style={{ width: 70 }} value={f.w[k]} onChange={(e) => setF({ w: { ...f.w, [k]: Math.max(0, Math.min(100, +e.target.value || 0)) } })} />
            </label>
          ))}
          <span className="sub">total {((f.w.scorecard ?? 0) + (f.w.quiz ?? 0) + (f.w.journal ?? 0))}%</span>
        </div>
      </div>
      <div className="field"><label>Assignment brief (students see this)</label><textarea className="txt" rows={3} style={{ width: '100%' }} value={f.brief} onChange={(e) => setF({ brief: e.target.value })} /></div>
      {updateClass.error ? <div className="sub" style={{ color: 'var(--bad)' }}>{(updateClass.error as Error).message}</div> : null}
      <button className="btn btn-pri" disabled={updateClass.isPending}
        onClick={() => updateClass.mutate({ name: f.name, simLength: f.simLength, brief: f.brief || null, gradeWeights: { ...f.w } })}>
        {updateClass.isPending ? 'Saving…' : 'Save settings'}
      </button>
    </div>
  );

  return (
    <div className="ic-root">
      <Link to="/dashboard" className="back">← My Classes</Link>
      <div className="spread head" style={{ flexWrap: 'wrap', gap: 10, margin: '6px 0 18px' }}>
        <div>
          <h1>{o.class.name}</h1>
          <div className="sub" data-tut="ic-joincode">Join code <b style={{ color: 'var(--acc)' }}>{o.class.joinCode}</b> · {o.kpis.enrolled} students · {o.class.simLength} quarters</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn-ghost btn-sm"
            title="Restart the console tour"
            onClick={() => useTutorial.getState().restart('instructor', shapeOf(instructorSeries))}
          >
            ? Tour
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv(o)}>⬇ Export CSV</button>
        </div>
      </div>

      <div className="kpi-strip">
        <div className="card"><div className="sub">Enrolled</div><div className="big-num">{o.kpis.enrolled}</div></div>
        <div className="card"><div className="sub">Started playing</div><div className="big-num">{o.kpis.started}</div></div>
        <div className="card"><div className="sub">Avg composite score</div><div className="big-num">{o.kpis.avgComposite != null ? Math.round(o.kpis.avgComposite) : '—'}</div></div>
        <div className="card"><div className="sub">Avg quarters played</div><div className="big-num">{o.kpis.avgQuarters != null ? o.kpis.avgQuarters.toFixed(1) : '—'}</div></div>
      </div>

      {/* The tab bar stays mounted while a Review drilldown is open: it is the
          way back out, and the console tour's Students/Grading/Settings steps
          anchor on these buttons right after the drilldown opens — they used to
          unmount with it, leaving the spotlight pointing at nothing. */}
      <div className="tabs">
        {TABS.map(([id2, l]) => (
          <button
            key={id2}
            data-tut={`ic-tab-${id2}`}
            className={tab === id2 && !reviewUserId ? 'on' : ''}
            // A one-time temp password does not outlive leaving the roster.
            onClick={() => { setTab(id2); setReviewUserId(null); setResetFor(null); }}
          >
            {l}
          </button>
        ))}
      </div>

      {reviewUserId
        ? <StudentReview classId={id!} userId={reviewUserId} onBack={() => setReviewUserId(null)} />
        : tab === 'leaderboard' ? leaderboardTab() : tab === 'students' ? studentsTab() : tab === 'teams' ? teamsTab() : tab === 'grading' ? gradingTab() : settingsTab()}

      <InstructorTutorial
        tab={tab}
        drilldownOpen={!!reviewUserId}
        className={o.class.name}
        studentCount={o.students.length}
        playedCount={playing.length}
      />
    </div>
  );
}

